import Papa from 'papaparse'
import * as XLSX from 'xlsx'

import { CSV_HEADERS } from '../types/dotationOrderForm'

export type CsvHeader = typeof CSV_HEADERS[number]

export type DotationCsvRow = {
  [key in CsvHeader]: string
} & {
  fieldErrors: Partial<Record<CsvHeader, string>>
}

type TranslateFn = (key: string, params?: Record<string, unknown>) => string

const CSV_DELIMITER = ';'
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const AMOUNT_PATTERN = /^\d+$/

export function createEmptyDotationCsvRow(): DotationCsvRow {
  return {
    firstname: '',
    lastname: '',
    gender: '',
    email: '',
    amount: '',
    customer_client_reference: '',
    fieldErrors: {}
  }
}

function isExcelFile(file: File): boolean {
  return /\.(xlsx|xls)$/i.test(file.name)
}

function normalizeRow(raw: Record<string, unknown>): DotationCsvRow {
  const row = createEmptyDotationCsvRow()
  CSV_HEADERS.forEach(header => {
    const value = raw[header]
    row[header] = value == null ? '' : String(value).trim()
  })
  return row
}

function isRowBlank(row: DotationCsvRow): boolean {
  return CSV_HEADERS.every(header => !row[header])
}

function parseCsvRows(file: File): Promise<Record<string, unknown>[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      delimiter: CSV_DELIMITER,
      skipEmptyLines: true,
      complete: results => resolve(results.data as Record<string, unknown>[]),
      error: reject
    })
  })
}

function parseExcelRows(file: File): Promise<Record<string, unknown>[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = event => {
      try {
        const buffer = event.target?.result
        if (!buffer) throw new Error('empty file')

        const workbook = XLSX.read(buffer, { type: 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        resolve(XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { raw: false, defval: '' }))
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = () => reject(new Error('read error'))
    reader.readAsArrayBuffer(file)
  })
}

export async function parseDotationCsvFile(file: File): Promise<DotationCsvRow[]> {
  const rawRows = isExcelFile(file) ? await parseExcelRows(file) : await parseCsvRows(file)
  return rawRows.map(normalizeRow).filter(row => !isRowBlank(row))
}

/** Mirrors App::MultiChoiceCardOrder::ParseCsv#check_fields on the backend. */
export function validateDotationRow(row: DotationCsvRow, t: TranslateFn): Partial<Record<CsvHeader, string>> {
  const errors: Partial<Record<CsvHeader, string>> = {}

  if (!row.firstname) errors.firstname = t('newDotationOrder.entry.csvTable.errors.missingFirstname')
  if (!row.lastname) errors.lastname = t('newDotationOrder.entry.csvTable.errors.missingLastname')

  if (!row.email) {
    errors.email = t('newDotationOrder.entry.csvTable.errors.missingEmail')
  } else if (!EMAIL_PATTERN.test(row.email)) {
    errors.email = t('newDotationOrder.entry.csvTable.errors.invalidEmail')
  }

  if (!row.amount || !AMOUNT_PATTERN.test(row.amount) || Number(row.amount) <= 0) {
    errors.amount = t('newDotationOrder.entry.csvTable.errors.invalidAmount')
  }

  return errors
}

export function rowsToCsvText(rows: DotationCsvRow[]): string {
  const data = rows.map(row => {
    const record: Record<string, string> = {}
    CSV_HEADERS.forEach(header => { record[header] = row[header] })
    return record
  })

  return Papa.unparse(data, { header: true, delimiter: CSV_DELIMITER })
}

export function rowsToExcelBlob(rows: DotationCsvRow[]): Blob {
  const data = rows.map(row => {
    const record: Record<string, string> = {}
    CSV_HEADERS.forEach(header => { record[header] = row[header] })
    return record
  })

  const worksheet = XLSX.utils.json_to_sheet(data, { header: [...CSV_HEADERS] })
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Commande')

  const arrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  return new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}
